import { useState, useEffect, useRef } from "react";
import { useAuth, useDrive } from "../App";
import { Sidebar } from "./Dashboard";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileText, Upload, Trash2, Loader2, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BankStatements() {
  const { accessToken } = useAuth();
  const { initialized, folderIds, uploadFile, listFiles, getFileAsJson, deleteFile } = useDrive();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (initialized && folderIds.bank_statements) {
      loadStatements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, folderIds]);

  const loadStatements = async () => {
    setLoading(true);
    try {
      const files = await listFiles(folderIds.bank_statements);
      const jsonFiles = files.filter(f => f.name.endsWith('.json'));
      
      const loadedStatements = [];
      for (const file of jsonFiles) {
        try {
          const data = await getFileAsJson(file.id);
          loadedStatements.push({
            ...data,
            fileId: file.id,
            fileName: file.name
          });
        } catch (e) {
          console.error("Error loading statement:", e);
        }
      }
      
      loadedStatements.sort((a, b) => b.period.localeCompare(a.period));
      setStatements(loadedStatements);
    } catch (error) {
      console.error("Error loading statements:", error);
      toast.error("Failed to load bank statements");
    } finally {
      setLoading(false);
    }
  };

  const handleFileImport = async (file) => {
    if (!file) return;

    const ext = file.name.toLowerCase().split('.').pop();
    if (!['csv', 'pdf'].includes(ext)) {
      toast.error("Invalid file type. Use CSV or PDF");
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const endpoint = ext === 'csv' ? '/api/parse/csv' : '/api/parse/pdf';
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error("Failed to parse file");
      }
      
      const result = await response.json();
      
      if (!result.success || !result.transactions?.length) {
        toast.error(result.error || "No transactions found in file");
        return;
      }
      
      // Group by month
      const transactionsByMonth = {};
      for (const txn of result.transactions) {
        const month = txn.date.slice(0, 7);
        if (!transactionsByMonth[month]) {
          transactionsByMonth[month] = [];
        }
        transactionsByMonth[month].push(txn);
      }
      
      // Save each month's transactions
      for (const [month, transactions] of Object.entries(transactionsByMonth)) {
        const existingFiles = await listFiles(folderIds.bank_statements);
        const existingFile = existingFiles.find(f => f.name === `${month}.json`);
        
        let existingData = { transactions: [] };
        if (existingFile) {
          existingData = await getFileAsJson(existingFile.id);
        }
        
        // Merge transactions (avoid duplicates by id)
        const existingIds = new Set(existingData.transactions.map(t => t.id));
        const newTransactions = transactions.filter(t => !existingIds.has(t.id));
        
        const statementData = {
          period: month,
          transactions: [...existingData.transactions, ...newTransactions],
          updatedAt: new Date().toISOString()
        };
        
        await uploadFile(
          `${month}.json`,
          JSON.stringify(statementData, null, 2),
          'application/json',
          folderIds.bank_statements,
          existingFile?.id
        );
      }
      
      toast.success(`Imported ${result.transactions.length} transactions`);
      setImportDialogOpen(false);
      loadStatements();
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import bank statement");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (statement) => {
    if (!confirm(`Delete all transactions for ${statement.period}?`)) return;

    try {
      await deleteFile(statement.fileId);
      toast.success("Bank statement deleted");
      loadStatements();
    } catch (error) {
      console.error("Error deleting statement:", error);
      toast.error("Failed to delete statement");
    }
  };

  if (!initialized) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="bank-statements" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeItem="bank-statements" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Bank Statements
              </h1>
              <p className="text-slate-600 mt-1">Import and manage bank transactions</p>
            </div>
            <Button
              data-testid="import-statement-btn"
              onClick={() => setImportDialogOpen(true)}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Statement
            </Button>
          </div>

          {/* Statements Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : statements.length === 0 ? (
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardContent className="flex flex-col items-center justify-center py-20">
                <FileText className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">No bank statements imported yet</p>
                <Button onClick={() => setImportDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Your First Statement
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {statements.map((statement, index) => {
                const totalExpenses = statement.transactions.filter(t => t.is_expense || t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
                const totalIncome = statement.transactions.filter(t => !t.is_expense && t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
                const unmatched = statement.transactions.filter(t => !t.isMatched).length;
                
                return (
                  <Card key={statement.fileId} data-testid={`statement-card-${index}`} className="bg-white border border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-slate-600" />
                        </div>
                        <Button
                          data-testid={`delete-statement-${index}`}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(statement)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {new Date(statement.period + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">{statement.transactions.length} transactions</p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Expenses:</span>
                          <span className="font-mono text-red-600">-£{totalExpenses.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Income:</span>
                          <span className="font-mono text-emerald-600">+£{totalIncome.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-100">
                          <span className="text-slate-500">Unmatched:</span>
                          <span className={`font-medium ${unmatched > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{unmatched}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Import Bank Statement</DialogTitle>
          </DialogHeader>
          <div
            className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer.files?.[0];
              if (file) handleFileImport(file);
            }}
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 text-blue-600 mx-auto animate-spin" />
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Drag & drop or click to upload</p>
                <p className="text-sm text-slate-400 mt-2">CSV or PDF bank statement</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileImport(file);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
